import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/api';

// One squad's picks and check-ins for a game, with switching between the fan's squads. Only
// the latest request's answer is applied; a 4xx means "no squad" rather than a failure.
const EMPTY = { data: null, settled: false, switching: false, switchFailed: false, loadFailed: false, pendingId: null };

function isRefusal(error) {
  const status = Number(error?.response?.status);
  return status >= 400 && status < 500;
}

export default function useGameSquad(eventId, squadId) {
  const [state, setState] = useState(EMPTY);
  const latest = useRef(0);

  const request = useCallback(
    async (id, { switching } = {}) => {
      const token = latest.current + 1;
      latest.current = token;
      if (switching) setState((current) => ({ ...current, switching: true, switchFailed: false, pendingId: id }));
      try {
        const data = await apiService.getScheduleEventSquad(eventId, id);
        if (latest.current !== token) return;
        setState({ data: data || null, settled: true, switching: false, switchFailed: false, loadFailed: false, pendingId: null });
      } catch (error) {
        if (latest.current !== token) return;
        if (switching) {
          setState((current) => ({ ...current, switching: false, switchFailed: true, pendingId: null }));
          return;
        }
        setState({ data: null, settled: true, switching: false, switchFailed: false, loadFailed: !isRefusal(error), pendingId: null });
      }
    },
    [eventId]
  );

  useEffect(() => {
    setState(EMPTY);
    if (eventId == null) return undefined;
    request(squadId || undefined);
    return () => {
      latest.current += 1;
    };
  }, [eventId, squadId, request]);

  const select = useCallback((id) => request(id, { switching: true }), [request]);
  const retry = useCallback(() => {
    setState((current) => ({ ...current, settled: false, loadFailed: false }));
    request(squadId || undefined);
  }, [request, squadId]);

  return {
    data: state.data,
    settled: state.settled,
    switching: state.switching,
    switchFailed: state.switchFailed,
    loadFailed: state.loadFailed,
    selectedId: state.pendingId || state.data?.squad?.id || null,
    select,
    retry,
  };
}
